import { FC } from 'react';
import { clsx } from 'clsx';
import { Link } from 'react-router';
import { MdOutlineDescription } from 'react-icons/md';
import Avatar, { AvatarGroup, AVATAR_SIZES } from '@srm/ui/avatar';
import Tooltip from '@srm/ui/tooltip';
import { RouteNames } from '@/router/router.types';
import type { IStatusBarPerson } from '@models/document/IDocumentStatusBar';
import KeyedUserAvatar from '@molecules/UserAvatar/KeyedUserAvatar';
import UserHoverCard from '@molecules/UserHoverCard/UserHoverCard';
import { branchLabel, performersLabel } from '@/utils/tenderStatus';
import type { TenderHolder } from './tenderStatusModel';

const FACE = AVATAR_SIZES.m;
const STACK = AVATAR_SIZES.s;
/** Сколько лиц в стопке до «+N» */
const STACK_LIMIT = 2;

/** Пропсы аватара исполнителя: группа — квадратом. */
const faceOf = (p: IStatusBarPerson) => ({
    userKey: p.key,
    name: p.fullName ?? p.name,
    type: p.isGroup ? 'group' : 'user',
});

const personLine = (p: IStatusBarPerson): string =>
    p.deputyFor ? `${p.deputyFor} отсутствует, замещает ${p.name}` : (p.fullName ?? p.name);

const Faces: FC<{ performers: readonly IStatusBarPerson[] }> = ({ performers }) => {
    if (performers.length === 0) return <Avatar size={FACE} color="gray" />;
    if (performers.length === 1) {
        return (
            <UserHoverCard userKey={performers[0].key} fallbackName={performers[0].fullName ?? performers[0].name}>
                <KeyedUserAvatar {...faceOf(performers[0])} size={FACE} />
            </UserHoverCard>
        );
    }
    return (
        <Tooltip
            label={performers.map((p) => (
                <div key={p.key}>{personLine(p)}</div>
            ))}
            maxWidth={320}
            openDelay={150}
        >
            <AvatarGroup
                className="tender-status__stack"
                spacing="sm"
                more={performers.length - STACK_LIMIT}
                moreSize={STACK}
            >
                {performers.slice(0, STACK_LIMIT).map((p) => (
                    <KeyedUserAvatar key={p.key} {...faceOf(p)} size={STACK} />
                ))}
            </AvatarGroup>
        </Tooltip>
    );
};

/** Строка «кто держит»: лицо, документ ветки, кто, что и к какому сроку. */
const TenderHolderRow: FC<{ holder: TenderHolder }> = ({ holder }) => {
    const { branch } = holder;
    const doc = !branch.isCurrent && !(holder.kind === 'task' && holder.continues) && (
        <div className="tender-status__doc">
            <Link to={RouteNames.DOCUMENT.replace(':id', branch.docId)}>{branchLabel(branch)}</Link>
            {holder.docState && ` ${holder.docState}`}
        </div>
    );

    if (holder.kind === 'doc') {
        return (
            <li className="tender-status__holder">
                <span className="tender-status__face">
                    <Avatar size={FACE} color="gray" icon={<MdOutlineDescription />} />
                </span>
                <div className="tender-status__holder-body">
                    {doc}
                    <div className="tender-status__when">Активных задач нет</div>
                </div>
            </li>
        );
    }

    const { task, due } = holder;
    const deputies = task.performers.filter((p) => p.deputyFor).map(personLine);
    return (
        <li className={clsx('tender-status__holder', holder.continues && 'tender-status__holder--continues')}>
            <span className="tender-status__face">
                <Faces performers={task.performers} />
            </span>
            <div className="tender-status__holder-body">
                {doc}
                <div className="tender-status__who">{performersLabel(task.performers)}</div>
                <div>{task.count > 1 ? `${task.name} (${task.count})` : task.name}</div>
                <div className="tender-status__when">
                    <span className={clsx(due.tone && `tender-status__due--${due.tone}`)}>{due.text}</span>
                    {deputies.length > 0 && `. ${deputies.join('; ')}`}
                </div>
            </div>
        </li>
    );
};

export default TenderHolderRow;
